import { RequestService } from 'Utils/Api';
import { mapPackages, mapPlan } from 'Utils/Plans';
import { parseSubscriptions } from 'Utils/Subscriptions';
import { logger } from 'Utils/Logger';
import { getSheerVerificationId } from 'Utils/SheerIdHelpers';
import { modifyAddressResponse } from 'Utils/Shipping';
import { isNonAnonymousUser, getAccessToken } from 'Utils/UserCookie';
import { getDefaultConfig } from 'Utils/Store';

// https://github.com/foxbroadcasting/apigateway/blob/master/v2.0/services/password.yaml
export const resetPassword = async (email, password, code) => {
  const { foxApi, accountApiKey } = getDefaultConfig();
  const payload = {
    code,
    email,
    password,
  };
  logger.apiRequest('reset password', { code });

  try {
    const res = await RequestService.send({
      url: `${foxApi}/reset-password`,
      method: 'POST',
      data: payload,
      customHeaders: { 'x-api-key': accountApiKey },
    });
    logger.apiResponse('reset pw success');
    return res;
  } catch (error) {
    logger.apiError('reset password error', null, error);
    console.error(error);
  }
};

// https://github.com/foxbroadcasting/apigateway/blob/master/v2.0/services/accountops_v1.yaml
export const getProfile = async (profileId) => {
  const { foxApi, accountApiKey } = getDefaultConfig();
  logger.apiRequest('get profile');
  try {
    const { data } = await RequestService.send({
      url: `${foxApi}/update/${profileId}`,
      method: 'GET',
      authenticate: true,
      customHeaders: { 'x-api-key': accountApiKey },
    });
    logger.apiResponse('get profile success');
    return data;
  } catch (error) {
    logger.apiError('get profile error', null, error);
    console.error(error.message);
    return false;
  }
};

// https://github.com/foxbroadcasting/apigateway/blob/master/v2.0/services/subscription.yaml
export const getSubscription = async (productSkus = {}) => {
  const { foxApi, accountApiKey } = getDefaultConfig();
  logger.apiRequest('subscription', productSkus);

  try {
    const subscription = await RequestService.send({
      url: `${foxApi}/hasSubscription`,
      method: 'POST',
      authenticate: true,
      customHeaders: { 'x-api-key': accountApiKey },
    });
    const subs = parseSubscriptions(subscription.data, productSkus);
    logger.apiResponse('subscription success', subs);
    return subs;
  } catch (error) {
    logger.apiError('subscription error', productSkus, error);
    return { error: error.message, hasSubscription: false, subscriptions: [] };
  }
};

// https://github.com/foxbroadcasting/apigateway/blob/master/v2.0/services/prospect.yaml
export const sendProspectEmail = async (email, url, prospect, platform) => {
  const { foxApi, accountApiKey } = getDefaultConfig();
  logger.apiRequest('send prospect email', { url, prospect, platform });
  const payload = {
    email,
    url,
    prospect,
    platform,
  };

  try {
    const res = await RequestService.send({
      url: `${foxApi}/prospect`,
      method: 'POST',
      data: payload,
      customHeaders: { 'x-api-key': accountApiKey },
    });
    logger.apiResponse('send prospect email success', res);
    return res;
  } catch (error) {
    logger.apiError('send prospect email error', null, error);
    return error;
  }
};

/*
This endpoint is like the register method in Profile SDK (which uses the /register endpoint),
but this endpoint also creates an account in Evergent
https://github.com/foxbroadcasting/apigateway/blob/master/v2.0/services/paymentaccount.yaml
*/
export const createAccount = async (user) => {
  const { foxApi, accountApiKey } = getDefaultConfig();
  const { firstName, lastName, password = '', email } = user;

  const payload = {
    CreateUserReqMessage: {
      firstName,
      lastName,
      customerUsername: email,
      customerPassword: password,
      email,
      isGenerateToken: false,
      dmaId: '001',
      verifyEmail: false,
      address: {
        country: 'US',
      },
    },
  };
  logger.apiRequest('create payment account');

  try {
    const res = await RequestService.send({
      url: `${foxApi}/paymentaccount`,
      method: 'POST',
      data: payload,
      customHeaders: { 'x-api-key': accountApiKey },
      authenticate: true,
    });

    const isSuccess = res.status === 200;
    logger.apiResponse('create payment account success', isSuccess || res.data);
    return isSuccess ? res.data.CreateUserResponseMessage : res;
  } catch (error) {
    logger.apiError('create payment account error', null, e);
    return error;
  }
};

/*
This endpoint is like the update method in Profile SDK (which uses the /update endpoint),
but this endpoint also updates the account in Evergent
https://github.com/foxbroadcasting/apigateway/blob/master/v2.0/services/paymentaccount.yaml
*/
export const updateAccount = async ({ profileId, firstName, lastName }) => {
  const { foxApi, accountApiKey } = getDefaultConfig();
  const payload = {
    UpdateProfileRequestMessage: {
      firstName,
      lastName,
      emailNotificationFlag: true,
    },
  };
  logger.apiRequest('update payment account', payload);

  try {
    const res = await RequestService.send({
      url: `${foxApi}/paymentaccount/${profileId}`,
      method: 'PUT',
      data: payload,
      customHeaders: {
        'x-api-key': accountApiKey,
      },
      authenticate: true,
    });
    const isSuccess = res.status === 200;
    logger.apiResponse('update payment account success', isSuccess || res.data);
    return { updateSuccess: isSuccess };
  } catch (error) {
    logger.apiError('update payment account error', null, e);
    return error;
  }
};

// https://github.com/foxbroadcasting/apigateway/blob/master/v2.0/services/d2cpackages.yaml
export const fetchPackages = async () => {
  const { foxApi, accountApiKey, subscriptionProduct } = getDefaultConfig();
  logger.apiRequest('fetch packages', subscriptionProduct);

  const sheerVerificationId = getSheerVerificationId();
  const queryParams = sheerVerificationId ? `?verificationID=${sheerVerificationId}` : '';

  try {
    const res = await RequestService.send({
      url: `${foxApi}/packages${queryParams}`,
      method: 'GET',
      customHeaders: {
        'x-api-key': accountApiKey,
        'x-subscription-product': subscriptionProduct,
      },
      authenticate: true,
    });

    const { PkgsOrProdsResponseMsg: data } = res.data;
    const { pkgWithPromos } = data;
    const isSuccess = data.responseMessage === 'SUCCESS';

    if (!isSuccess || !pkgWithPromos.length) {
      throw Error('No packages returned');
    }
    const packages = mapPackages(pkgWithPromos);
    logger.apiResponse('fetch packages response success', packages);
    return packages;
  } catch (error) {
    logger.apiError('fetch packages error', null, error);
    console.error(`packages API error: ${error.message}`);
    return [];
  }
};

// https://github.com/foxbroadcasting/apigateway/blob/master/v2.0/services/subscription.yaml
export const getSubscriptionStatus = async () => {
  const { foxApi, accountApiKey, subscriptionProduct } = getDefaultConfig();
  logger.apiRequest('subscription status request', subscriptionProduct);

  try {
    const res = await RequestService.send({
      url: `${foxApi}/currentSubscriptionStatus`,
      method: 'GET',
      customHeaders: {
        'x-api-key': accountApiKey,
        'x-subscription-product': subscriptionProduct,
      },
      authenticate: true,
    });

    const { data } = res;
    logger.apiResponse('subscription status success', data);
    return data;
  } catch (error) {
    logger.apiError('subscription status error', null, error);
    console.error(`get subscription API error: ${error}`);
    return [];
  }
};

// https://github.com/foxbroadcasting/apigateway/blob/master/v2.0/services/subscription.yaml
export const removeCustomerSegment = async (segmentName) => {
  const { foxApi, accountApiKey } = getDefaultConfig();
  const token = isNonAnonymousUser() && getAccessToken();
  logger.apiRequest('remove customer segment', segmentName);
  const payload = {
    channelPartnerID: 'FOXUSA',
    segmentName,
  };

  try {
    const res = await fetch(`${foxApi}/removeCustomerSegment`, {
      method: 'POST',
      body: JSON.stringify(payload),
      keepalive: true,
      headers: {
        'x-api-key': accountApiKey,
        Authorization: `Bearer ${token}`,
        Accept: 'application/json, text/plain, */*',
      },
    });
    const { data } = res;
    logger.apiResponse('remove customer segment success', data);
    return data;
  } catch (error) {
    logger.apiError('remove customer segment error', null, error);
    console.error('remove segment error: ', error);
    return error;
  }
};

// https://github.com/foxbroadcasting/apigateway/blob/master/v2.0/services/subscription.yaml
export const getSubscriptionPauseThresholds = async () => {
  const { foxApi, accountApiKey, subscriptionProduct } = getDefaultConfig();
  logger.apiRequest('pause thresholds');

  try {
    const res = await RequestService.send({
      url: `${foxApi}/subscriptionPauseThresholds`,
      method: 'GET',
      customHeaders: {
        'x-api-key': accountApiKey,
        'x-subscription-product': subscriptionProduct,
      },
      authenticate: true,
    });
    const { data } = res;
    logger.apiResponse('pause thresholds success', data);
    return data;
  } catch (error) {
    logger.apiError('pause thresholds error', null, error);
    console.error(`API: ${error}`);
    return [];
  }
};

// https://github.com/foxbroadcasting/apigateway/blob/master/v2.0/services/subscription.yaml
export const pauseSubscription = async ({ numOfDays, serviceID }) => {
  const { foxApi, accountApiKey } = getDefaultConfig();
  logger.apiRequest('pause subscription', numOfDays);
  const payload = {
    noOfDays: numOfDays,
    serviceType: 'PACKAGE',
    serviceID,
  };

  try {
    const res = await RequestService.send({
      url: `${foxApi}/pauseSubscription`,
      method: 'POST',
      customHeaders: {
        'x-api-key': accountApiKey,
      },
      authenticate: true,
      data: payload,
    });
    // There will be no response data object, only status
    const isSuccess = res.status === 200;
    logger.apiResponse('pause subscription success', res);
    return { isSuccess };
  } catch (error) {
    logger.apiError('pause subscription error', null, error);
    console.error('pause subscription error: ', error);
    return error;
  }
};

// https://github.com/foxbroadcasting/apigateway/blob/master/v2.0/services/subscription.yaml
export const resumeSubscription = async (serviceID) => {
  const { foxApi, accountApiKey } = getDefaultConfig();
  logger.apiRequest('resume subscription', serviceID);
  const payload = {
    serviceType: 'PACKAGE',
    serviceID,
  };

  try {
    const res = await RequestService.send({
      url: `${foxApi}/resumeSubscription`,
      method: 'POST',
      customHeaders: {
        'x-api-key': accountApiKey,
      },
      authenticate: true,
      data: payload,
    });
    // There will be no response data object, only status
    const isSuccess = res.status === 200;
    logger.apiResponse('resume subscription success', res);
    return { isSuccess };
  } catch (error) {
    logger.apiError('resume subscription error', null, error);
    return error;
  }
};

// https://github.com/foxbroadcasting/apigateway/blob/master/v2.0/services/subscription.yaml
export const cancelPauseSubscription = async (serviceID) => {
  const { foxApi, accountApiKey } = getDefaultConfig();
  logger.apiRequest('cancel pause', serviceID);
  const payload = {
    serviceType: 'PACKAGE',
    serviceID,
  };

  try {
    const res = await RequestService.send({
      url: `${foxApi}/cancelPauseSubscription`,
      method: 'POST',
      customHeaders: {
        'x-api-key': accountApiKey,
      },
      authenticate: true,
      data: payload,
    });
    // There will be no response data object, only status
    const isSuccess = res.status === 200;
    logger.apiResponse('cancel pause success', res);
    return { isSuccess };
  } catch (error) {
    logger.apiError('cancel pause error', error.message);
    console.error('cancel pause error: ', error);
    return { isSuccess: false };
  }
};

// https://github.com/foxbroadcasting/apigateway/blob/master/v2.0/services/discountverification.yaml
export const addSheerVerificationId = async (verificationID) => {
  const { foxApi, accountApiKey } = getDefaultConfig();
  logger.apiRequest('add sheer verification Id', verificationID);
  try {
    const { data } = await RequestService.send({
      url: `${foxApi}/verification`,
      method: 'POST',
      data: { verificationID },
      authenticate: true,
      customHeaders: {
        'x-api-key': accountApiKey,
      },
    });
    logger.apiResponse('add sheer verification Id success', data);
    return data;
  } catch (error) {
    console.error(error.message);
    logger.apiError('add sheer verification Id error', null, error.message);
    return { error: error.message };
  }
};

// https://github.com/foxbroadcasting/apigateway/blob/master/v2.0/services/discountverification.yaml
export const getSheerUserInfo = async (verificationID) => {
  const { foxApi, accountApiKey } = getDefaultConfig();
  logger.apiRequest('get sheer user info', verificationID);
  try {
    const { data } = await RequestService.send({
      url: `${foxApi}/verification/${verificationID}/details`,
      method: 'GET',
      customHeaders: {
        'x-api-key': accountApiKey,
      },
    });
    logger.apiResponse('get sheer user info success', data);
    return data;
  } catch (error) {
    console.error(error.message);
    logger.apiError('get sheer user info error', null, error.message);
    return { error: error.message };
  }
};

// https://github.com/foxbroadcasting/apigateway/blob/master/v2.0/services/discountverification.yaml
export const getSheerVerificationIdType = async (verificationID) => {
  const { foxApi, accountApiKey } = getDefaultConfig();
  logger.apiRequest('get sheer id type', verificationID);
  try {
    const { data } = await RequestService.send({
      url: `${foxApi}/checkverification?verificationID=${verificationID}`,
      method: 'GET',
      customHeaders: {
        'x-api-key': accountApiKey,
      },
    });
    logger.apiResponse('get sheer id type success', data);
    return data[0] || {};
  } catch (error) {
    console.error(error.message);
    logger.apiError('get sheer id type error', null, error.message);
    return { error: error.message };
  }
};

// https://github.com/foxbroadcasting/apigateway/blob/master/v2.0/services/gifts.yaml
export const verifyGiftCode = async (giftCode) => {
  const { foxApi, accountApiKey } = getDefaultConfig();
  const payload = { pin: giftCode };
  logger.apiRequest('giftcard status', payload);
  try {
    const { data } = await RequestService.send({
      url: `${foxApi}/giftcard/status`,
      method: 'POST',
      data: payload,
      customHeaders: {
        'x-api-key': accountApiKey,
      },
      authenticate: true,
    });
    logger.apiResponse('giftcard status success', data);
    return data;
  } catch (error) {
    if (error.response && error.response.data) {
      logger.apiError('giftcard status error', error.response.data, error);
      return Promise.reject(error.response.data.errorMessage);
    }
    logger.apiError('giftcard status error', null, error);
    return Promise.reject(error);
  }
};

// https://github.com/foxbroadcasting/apigateway/blob/master/v2.0/services/gifts.yaml
export const redeemGift = async (payload) => {
  const { foxApi, accountApiKey } = getDefaultConfig();
  logger.apiRequest('giftcard redeem', payload);
  try {
    const { data } = await RequestService.send({
      url: `${foxApi}/giftcard/redeem`,
      method: 'PUT',
      data: payload,
      customHeaders: {
        'x-api-key': accountApiKey,
      },
      authenticate: true,
    });
    logger.apiResponse('giftcard redeem success', data);
    return data;
  } catch (error) {
    if (error.response && error.response.data) {
      logger.apiError('giftcard redeem error', error.response.data, error);
      return Promise.reject(error.response.data.errorMessage);
    }
    logger.apiError('giftcard redeem error', null, error);
    return Promise.reject(error);
  }
};

// https://github.com/foxbroadcasting/apigateway/blob/master/v2.0/services/subscription.yaml
export const removeSubscription = async (serviceID) => {
  const { foxApi, accountApiKey } = getDefaultConfig();
  const payload = {
    serviceID,
    serviceType: 'PACKAGE',
  };
  logger.apiRequest('remove subscription', payload);

  try {
    const res = await RequestService.send({
      url: `${foxApi}/removeSubscription`,
      method: 'POST',
      data: payload,
      customHeaders: {
        'x-api-key': accountApiKey,
      },
      authenticate: true,
    });
    // There will be no response data object, only status.
    const isSuccess = res.status === 204;
    logger.apiResponse('remove subscription success', res);
    return isSuccess;
  } catch (error) {
    if (error.response && error.response.data) {
      logger.apiError('remove subscription error', error.response.data, error);
      return Promise.reject(error.response.data.errorMessage);
    }
    logger.apiError('remove subscription error', null, error);
    return Promise.reject(error);
  }
};

// https://github.com/foxbroadcasting/apigateway/blob/master/v2.0/services/subscription.yaml
export const reactivateSubscription = async (serviceID, notifyEmail = true) => {
  const { foxApi, accountApiKey } = getDefaultConfig();
  const payload = { serviceID, notifyEmail, serviceType: 'PACKAGE' };
  logger.apiRequest('reactivate subscription', serviceID);

  try {
    const res = await RequestService.send({
      url: `${foxApi}/reactivateSubscription`,
      method: 'POST',
      data: payload,
      customHeaders: {
        'x-api-key': accountApiKey,
      },
      authenticate: true,
    });
    const isSuccess = res.status === 200;
    logger.apiResponse('reactivate subscription success', res);
    return { isSuccess };
  } catch (error) {
    logger.apiError('reactivate subscription error', null, error);
    return { isSuccess: false };
  }
};

// https://github.com/foxbroadcasting/apigateway/blob/master/v2.0/services/subscription.yaml
export const getChurnedRestartableSubscription = async () => {
  const { foxApi, accountApiKey } = getDefaultConfig();
  logger.apiRequest('get churned sub');

  try {
    const res = await RequestService.send({
      url: `${foxApi}/getRestartableSubscription`,
      method: 'GET',
      customHeaders: {
        'x-api-key': accountApiKey,
      },
      authenticate: true,
    });
    const { GetRestartableSubscriptionResponseMessage: data } = res.data;
    logger.apiResponse('get churned sub success', data);

    const { isEligibleForRestart = false, paymentMethodRequired = false, pkgMsg = {}, churnedPkgMsg = {} } = data;

    const restartPlan = mapPlan({ pkgMsg });

    return {
      isEligibleForRestart,
      paymentMethodRequired,
      restartPlan,
      churnedPlan: churnedPkgMsg,
    };
  } catch (error) {
    logger.apiError('get churned sub error', error);
    throw new Error(error);
  }
};

// https://github.com/foxbroadcasting/apigateway/blob/master/v2.0/services/address.yaml
export const verifyAddress = async ({ address1, address2 = '', city, state, zipCode }) => {
  const { foxApi, accountApiKey } = getDefaultConfig();
  const params = {
    address1,
    address2,
    city,
    state,
    zipCode,
  };
  const query = Object.entries(params)
    .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
    .join('&');

  try {
    logger.apiRequest('verify addr request');
    const res = await RequestService.send({
      url: `${foxApi}/verify?${query}`,
      method: 'GET',
      customHeaders: { 'x-api-key': accountApiKey },
      authenticate: true,
    });

    logger.apiResponse('verify addr success', res);
    const { data } = res;
    const formatAddress = modifyAddressResponse(data);
    return formatAddress;
  } catch (error) {
    logger.apiError('verify addr error', error);
    throw new Error(error);
  }
};

// https://github.com/foxbroadcasting/apigateway/blob/master/v2.0/services/bangosubscription.yaml
export const addBangoSubscription = async (subscriptionRequestId) => {
  const { foxApi, accountApiKey } = getDefaultConfig();
  logger.apiRequest('add bango subscription', subscriptionRequestId);

  try {
    const res = await RequestService.send({
      url: `${foxApi}/bango/addSubscription`,
      method: 'POST',
      data: { subscriptionRequestId },
      customHeaders: {
        'x-api-key': accountApiKey,
      },
      authenticate: true,
    });
    // There will be no response data object, only status.
    const isSuccess = res.status === 200;
    logger.apiResponse('add bango subscription success', res);
    return { isSuccess };
  } catch (error) {
    logger.apiError('add bango subscription error', null, error);
    return { isSuccess: false };
  }
};

// https://github.com/foxbroadcasting/apigateway/blob/master/v2.0/services/subscription.yaml
export const getPayments = async () => {
  const { foxApi, accountApiKey } = getDefaultConfig();
  logger.apiRequest('get payments');

  try {
    const res = await RequestService.send({
      url: `${foxApi}/getPayments`,
      method: 'GET',
      customHeaders: { 'x-api-key': accountApiKey },
      authenticate: true,
    });
    const { data } = res;
    // Response contains PII, do not log
    logger.apiResponse('get payments success');
    return data;
  } catch (error) {
    logger.apiError('get payments error', error);
    throw new Error(error);
  }
};

// https://github.com/foxbroadcasting/apigateway/blob/master/v2.0/services/subscription.yaml
export const getPaymentMethods = async () => {
  const { foxApi, accountApiKey } = getDefaultConfig();
  logger.apiRequest('get pmt methods');

  try {
    const res = await RequestService.send({
      url: `${foxApi}/getPaymentMethods`,
      method: 'GET',
      customHeaders: { 'x-api-key': accountApiKey },
      authenticate: true,
    });
    const { PaymentMethods } = res.data;
    if (PaymentMethods.length === 0) {
      return [];
    }
    // Response contains PII, do not log
    logger.apiResponse('get payment methods success');
    // filter labels: 'Card' and 'PayPal Account' for web platform.
    // 'OtherPaymentMethod' is present when user have IAP payment method.
    const paymentMethods = PaymentMethods.filter((pm) => {
      const validPaymentMethod = ['Card', 'PayPal Account', 'Apple Pay'].includes(pm.label);
      return pm.status === 'Active' && pm.isAutoPayment && validPaymentMethod;
    });

    const hasPaymentMethods = paymentMethods.length > 0;
    const methods = hasPaymentMethods ? paymentMethods : [];
    const hasCardPayment = methods.some((method) => method.cardInfoMessage);
    const hasPaypalPayment = methods.some((method) => method.PayPalAccountMessage);
    const hasApplePayPayment = methods.some((method) => method.ApplePayInfoMessage);
    const cardPayment = hasCardPayment && methods.find((method) => method.cardInfoMessage).cardInfoMessage;
    const paypalPayment =
      hasPaypalPayment && methods.find((method) => method.PayPalAccountMessage).PayPalAccountMessage;
    const hasGiftingPayment = PaymentMethods.some((pm) => pm.status === 'Active' && pm.label === 'Fox Payment');

    return {
      methods,
      hasPaymentMethods,
      hasCardPayment,
      hasPaypalPayment,
      hasApplePayPayment,
      cardPayment,
      paypalPayment,
      hasGiftingPayment,
    };
  } catch (error) {
    logger.apiError('get payment methods error', error);
    if (error.response && error.response.data) {
      return {
        isValid: false,
        errorMessage: error.response.data.errorMessage,
      };
    }
  }
};

// https://github.com/foxbroadcasting/apigateway/blob/master/v2.0/services/subscription.yaml
export const getPromoDetails = async (serviceID, voucherCode) => {
  const { foxApi, accountApiKey } = getDefaultConfig();
  const payload = {
    serviceType: 'PACKAGE',
    serviceID,
    voucherCode,
  };

  logger.apiRequest('voucher details', payload);

  try {
    const res = await RequestService.send({
      url: `${foxApi}/getVoucherDetails`,
      method: 'POST',
      data: payload,
      customHeaders: { 'x-api-key': accountApiKey },
      authenticate: true,
    });
    const { data } = res;
    const { applicablePlans = [], couponCodeDetails = {} } = data;
    logger.apiResponse('voucher details success', { applicablePlans, couponCodeDetails });
    if (applicablePlans.length > 0) {
      const [{ nextBillingDateTime, priceToBeCharged }] = applicablePlans;
      const { descrip, discountType = '' } = couponCodeDetails;
      const isFlatDiscount = discountType.toLowerCase() === 'flat';

      return {
        isValid: true,
        nextBillingDateTime,
        priceToBeCharged,
        promoDescription: descrip,
        isFlatDiscount,
      };
    }
  } catch (error) {
    if (error.response && error.response.data) {
      logger.apiError('voucher details error', error.response.data, error);
      return {
        isValid: false,
        errorMessage: error.response.data.errorMessage,
      };
    }
  }
};

// https://github.com/foxbroadcasting/apigateway/blob/master/v2.0/services/subscription.yaml
export const getProrate = async ({ serviceID, zipCode }) => {
  const { foxApi, accountApiKey } = getDefaultConfig();
  logger.apiRequest('get prorate', { serviceID, zipCode });

  try {
    const res = await RequestService.send({
      url: `${foxApi}/getProrate?serviceID=${serviceID}&zipCode=${zipCode}`,
      method: 'GET',
      customHeaders: { 'x-api-key': accountApiKey },
      authenticate: true,
    });
    logger.apiResponse('get prorate success', res);
    return res;
  } catch (error) {
    logger.apiError('get prorate error', err);
  }
};
